import { Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { GlobalFunctions, SnackType } from '@global/GlobalFunctions';
import { ActionsDataService } from '@services/actions-data.service';
import { AssetsDataService, LevelAsset } from '@services/assets-data.service';
import { AuthenticationService, WebAppUser } from '@services/authentication.service';
import { combineLatest, takeUntil, distinctUntilChanged, filter, Subject, finalize, firstValueFrom } from 'rxjs';
import { siteSubscriptions } from '@services/siteSubscriptions';
import { Account } from '@services/accounts.service';
import { Location } from '@angular/common';
import { convertEpochToDateString } from '@functions/functions';
import { CreateAssetDistributionComponent } from '../create-asset-distribution/create-asset-distribution.component';
import { TestDistributionComponent } from '../test-distribution/test-distribution.component';
import { AssetDistribution, CreateModelData, ModelsService, ModelType } from '@services/models.service';
import { DropDownList, ListBoxComponent } from '@syncfusion/ej2-angular-dropdowns';

export class AssetModelsData {
	asset?: LevelAsset;
}

@Component({
	selector: 'app-asset-models',
	templateUrl: './asset-models.component.html',
	styleUrls: ['./asset-models.component.scss']
})
export class AssetModelsComponent implements OnInit, OnDestroy {

	/** MainLocations */
	public modelTypes: ModelType[] = [];

	/** locationFields */
	public modelFields: { text: string, value: string } = { text: "ModelName", value: "ModelTypeID" };

	/** Selected Account ID */
	public selectedAccountUID: string = ""

	/** Selected Organisation ID */
	public selectedOrganisationUID: string = ""

	/** Selected Asset */
	public selectedAsset: LevelAsset;

	/** Observable Subject Finalise */
	private finalise = new Subject<void>();

	/** account */
	public account: Account;

	/** currentUser */
	public currentUser: WebAppUser;

	/** assetDistributions */
	public assetDistributions: AssetDistribution[] = [];

	/** selectedModelType */
	public selectedModelType?: ModelType;

	/** shortMonths */
	private shortMonths: string[] = [
		"Jan", "Feb", "Mar", "Apr", "May", "Jun",
		"Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
	];

	/** oldModels */
	public oldModels: any[] = [];

	/** dropdown */
	@ViewChild('dropdown') dropdown: DropDownList;

	/** noActiveModel */
	public noActiveModel: boolean = false;

	/** selectedModel */
	public selectedModel: AssetDistribution | any;

	/** otherModels */
	public otherModels: any[] = [];

	/** listbox */
	@ViewChild('listbox') listbox: ListBoxComponent;

	/** gotInitialModel */
	public gotInitialModel: boolean = false;

	/** activeModels */
	public activeModels: any[] = [];

	/**
	 * Default constructor
	 * @param route route 
	 * @param router router 
	 * @param assetsDataService assetsDataService  
	 * @param siteSubscriptions siteSubscriptions 
	 * @param actionsDataService actionsDataService 
	 * @param dialog dialog 
	 * @param authenticationService authenticationService  
	 * @param globalFunctions globalFunctions 
	 * @param location location 
	 */
	constructor(
		private route: ActivatedRoute,
		private router: Router,
		public assetsDataService: AssetsDataService,
		public modelsService: ModelsService,
		public siteSubscriptions: siteSubscriptions,
		public actionsDataService: ActionsDataService,
		public dialog: MatDialog,
		private authenticationService: AuthenticationService,
		private globalFunctions: GlobalFunctions,
		private location: Location,
		public dialogRef: MatDialogRef<AssetModelsComponent>,
		@Inject(MAT_DIALOG_DATA) public dialogData: AssetModelsData,
	) {
		this.globalFunctions.displaySpinner(true);
		combineLatest([
			this.siteSubscriptions.selectedAccountGUID$.pipe(takeUntil(this.finalise), distinctUntilChanged()),
			this.siteSubscriptions.selectedAccount$.pipe(takeUntil(this.finalise), distinctUntilChanged()),
			this.siteSubscriptions.selectedOrganisationUID$.pipe(takeUntil(this.finalise), distinctUntilChanged()),
			this.authenticationService.currentUser$.pipe(takeUntil(this.finalise), distinctUntilChanged()),
			this.route.queryParams.pipe(takeUntil(this.finalise), distinctUntilChanged()),
			this.route.paramMap.pipe(takeUntil(this.finalise), distinctUntilChanged())
		]).pipe(
			// Ensure that subscriptions are not null or undefined
			filter(([selectedAccountGUID, selectedAccount, selectedOrganisationUID, currentUser, queryParams, paramMap]) =>
				!!selectedAccountGUID && !!selectedAccount && !!selectedOrganisationUID && !!currentUser && !!queryParams)
		).subscribe({
			next: ([selectedAccountGUID, selectedAccount, selectedOrganisationUID, currentUser, queryParams, paramMap]) => {
				this.selectedAccountUID = selectedAccountGUID;
				this.selectedOrganisationUID = selectedOrganisationUID;
				this.currentUser = currentUser;
				this.account = selectedAccount;
				this.account = this.siteSubscriptions.normalizeFeatures(this.account) as Account;

				this.getModelTypes();
			},
			error: (error) => {
				this.globalFunctions.HandleError(error, "Error: Error in one of the combined subscriptions", false);
			}
		});

		this.siteSubscriptions.updateModelList$.pipe(
			takeUntil(this.finalise), distinctUntilChanged(),
			filter((refresh) => !!refresh)
		).subscribe({
			next: () => {
				if (this.selectedModelType?.ModelTypeID == 1) {
					this.getAssetDistributions();
				}
				this.siteSubscriptions.UpdateModelList(false);
			},
			error: (error) => {
				this.globalFunctions.HandleError(error, "Error: Error in updateModelList$", false);
			}
		});
	}

	/** ngOnInit */
	ngOnInit(): void {
		if (this.dialogData && this.dialogData.asset) {
			this.selectedAsset = this.dialogData.asset;
		}
	}

	/**
	 * Page On Destroy Event Handler
	 */
	ngOnDestroy(): void {
		try {
			// Unsubscribe from all subscriptions
			this.finalise.next();
			this.finalise.complete();
		}
		catch { }
	}

	/**
	 * Cancel button for dialog
	 */
	onCancelClick(): void {
		if (this.dialogRef) {
			this.dialogRef.close();
		}
	}

	/**
	 * getModelTypes
	 */
	getModelTypes() {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		this.modelsService.getModelTypes$(this.selectedAccountUID, this.selectedOrganisationUID).pipe(
			takeUntil(fetchDataSubject), distinctUntilChanged(),
			filter((modelTypes) => !!modelTypes),
			finalize(() => {
				// Ensure the subscription is terminated and the subject is completed
				fetchDataSubject.next();
				fetchDataSubject.complete();
				this.globalFunctions.displaySpinner(false);
			})
		).subscribe({
			next: (modelTypes) => {
				this.modelTypes = modelTypes;
				if (this.modelTypes.length == 1) {
					this.selectedModelType = this.modelTypes[0];
					setTimeout(() => {
						this.dropdown.value = this.selectedModelType!.ModelTypeID;
					}, 1);
				}
			},
			error: (error) => { this.globalFunctions.HandleError(error, "Error: this.assetsDataService.postAssetDistribution$", false); },
		});
	}

	/**
	 * modelTypeChange
	 * @param event event 
	 */
	modelTypeChange(event: any) {
		this.selectedModelType = this.modelTypes.find(x => x.ModelTypeID === event.value);
		if (this.selectedModelType?.ModelTypeID == 1) {
			this.getAssetDistributions();
		}
	}

	/**
	 * refreshModel
	 */
	refreshModels() {
		;
		if (this.selectedModelType?.ModelTypeID == 1) {
			this.getAssetDistributions();
		}
	}

	/**
	 * getAssetDistributions
	 */
	getAssetDistributions() {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		this.assetDistributions = [];

		this.modelsService.getAssetDistribution$(this.selectedAccountUID, this.selectedOrganisationUID, this.selectedAsset.AssetID, 0, false).pipe(
			takeUntil(fetchDataSubject), distinctUntilChanged(),
			filter((distributions) => !!distributions),
			finalize(() => {
				// Ensure the subscription is terminated and the subject is completed
				fetchDataSubject.next();
				fetchDataSubject.complete();
				this.globalFunctions.displaySpinner(false);
				this.getOldModels();
			})
		).subscribe({
			next: (distributions) => {
				// this.assetDistributions = distributions.map((x: AssetDistribution) => ({
				// 	...x,
				// 	Distribution: JSON.parse(x.Distribution)
				// }));
				this.assetDistributions = distributions;
				this.getInitialModel();
			},
			error: (error) => {
				if (error.status !== 404) {
					this.globalFunctions.HandleError(error, "Error: this.assetsDataService.getAssetDistribution$", false);
				}
			},
		});
	}

	/**
	 * getListOfModels
	 * @returns 
	 */
	getListOfModels(): any[] {
		if (this.selectedModelType?.ModelTypeID == 1) {
			return this.assetDistributions;
		} else {
			return [];
		}
	}

	/**
	 * convertEpoch
	 * @param epoch
	 * @returns
	 */
	convertEpoch(epoch: number): string {
		/** Use the Global Function */
		return convertEpochToDateString(epoch);
	}

	/**
	 * getFields
	 * @returns 
	 */
	getFields(): { text: string, value: string } {
		if (this.selectedModelType && this.selectedModelType.ModelTypeID == 1) {
			return { text: "AssetName", value: "AssetDistributionID" };
		} else {
			return { text: "", value: "" };
		}
	}

	/**
	 * getOldModels
	 * @returns 
	 */
	getOldModels() {
		if (this.selectedModelType?.ModelTypeID == 1) {
			this.oldModels = this.assetDistributions.filter(x => x.MostRecent === false);
		} else {
			this.oldModels = [];
		}
	}

	/**
	 * goToModel
	 * @param model model 
	 */
	goToModel(model: any) {
		if (this.selectedModelType?.ModelTypeID == 1) {
			this.goToDistribution(model);
		}
	}

	/**
	 * goToDistribution
	 * @param dist dist 
	 */
	goToDistribution(dist: AssetDistribution) {
		let data = {
			asset: this.selectedAsset,
			viewing: true,
			distributionID: dist.AssetDistributionID,
			existingDistribution: dist
		}

		const dialogRef = this.dialog.open(CreateAssetDistributionComponent
			, { data: data }
		);

		dialogRef.afterClosed().pipe(
			filter(result => !!result)
		).subscribe(result => {
		});
	}

	/** getShortDate */
	getShortDate(date: Date): string {
		let result: string = "";
		result += date.getDate();
		result +=
			" " +
			this.shortMonths[date.getMonth()] +
			" " +
			date.getHours().toString().padStart(2, "0") +
			":" +
			date.getMinutes().toString().padStart(2, "0");

		return result;
	}

	/**
	 * getDateFromEpoch
	 * @param epoch epoch 
	 * @returns 
	 */
	getDateFromEpoch(epoch: number): Date {
		return new Date(epoch);
	}

	/**
	 * getDateWithoutTime
	 * @param epoch epoch 
	 * @returns 
	 */
	getDateWithoutTime(epoch: number): string {
		const date = new Date(epoch);
		return `${date.getDate().toString().padStart(2, '0')} ${this.shortMonths[date.getMonth()]} ${date.getFullYear()}`;
	}

	/**
	 * createNewModel
	 */
	createNewModel() {
		if (this.selectedModelType?.ModelTypeID === 1) {
			this.createNewDistribution();
		}
	}

	/**
	 * createNewDistribution
	 */
	createNewDistribution() {
		let data = {
			asset: this.selectedAsset
		}

		const dialogRef = this.dialog.open(CreateAssetDistributionComponent
			, { data: data }
		);

		dialogRef.afterClosed().pipe(
			filter(result => !!result)
		).subscribe(result => {
			let input: CreateModelData = {
				data: result.distributionData,
				tolerance: result.tolerance,
				sensitivity: result.sensitivity,
				isAutomatic: result.isAutomatic,
				automaticWindowWidth: result.automaticWindowWidth,
				remind: result.remind,
				remindAfter: result.remind ? result.remindAfter : 0,
				recreationFrequency: result.recreationFrequency ? result.recreationFrequency : 0,
				overlapPeriod: result.overlapPeriod ? result.overlapPeriod : 0,
				datapointIDs: result.datapoints ? result.datapoints.toString() : ""
			}
			this.postDistributionData(input);
		});
	}

	/**
	 * postDistributionData
	 * @param data data 
	 */
	postDistributionData(inputData: CreateModelData) {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		this.modelsService.postAssetDistribution$(this.selectedAccountUID, this.selectedOrganisationUID, this.selectedAsset.AssetID, inputData).pipe(
			takeUntil(fetchDataSubject), distinctUntilChanged(),
			filter((response) => !!response),
			finalize(() => {
				// Ensure the subscription is terminated and the subject is completed
				fetchDataSubject.next();
				fetchDataSubject.complete();
				this.getAssetDistributions();
			})
		).subscribe({
			next: (result) => {
				if (result[0].Result.includes("OK")) {
					this.globalFunctions.DisplaySnackBar({
						message: "Asset Model Created",
						type: SnackType.Info,
						duration: 1000
					});
				}
			},
			error: (error) => { this.globalFunctions.HandleError(error, "Error: this.assetsDataService.postAssetDistribution$", false); },
		});
	}

	/**
	 * openTestDistributionWindow
	 */
	openTestDistributionWindow(distribution: AssetDistribution) {
		let data = {
			Asset: this.selectedAsset,
			Distribution: distribution
		}

		const dialogRef = this.dialog.open(TestDistributionComponent
			, { data: data }
		);

		dialogRef.afterClosed().pipe(
			filter(result => !!result)
		).subscribe(result => {
		});

	}

	/**
	 * recreateModel
	 * @param distribution distribution 
	 */
	recreateModel(distribution: AssetDistribution) {
		let data = {
			asset: this.selectedAsset,
			viewing: false,
			editing: true,
			distributionID: distribution.AssetDistributionID,
			existingDistribution: distribution
		}

		const dialogRef = this.dialog.open(CreateAssetDistributionComponent
			, { data: data }
		);

		dialogRef.afterClosed().pipe(
			filter(result => !!result)
		).subscribe(result => {
			this.patchDistribution(distribution, result.tolerance, result.sensitivity, result.recreationFrequency, result.overlapPeriod);
		});
	}

	/**
	 * patchDistribution
	 * @param tolerance tolerance 
	 * @param sensitivity sensitivity 
	 * @param recreationFrequency recreationFrequency 
	 * @param overlapPeriod overlapPeriod 
	 */
	patchDistribution(distribution: AssetDistribution, tolerance: number, sensitivity: number, recreationFrequency: number, overlapPeriod: number) {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		this.modelsService.patchDistribution$(this.selectedAccountUID, this.selectedOrganisationUID, distribution.AssetDistributionID,
			tolerance, sensitivity, recreationFrequency, overlapPeriod).pipe(
				takeUntil(fetchDataSubject), distinctUntilChanged(),
				filter((response) => !!response),
				finalize(() => {
					// Ensure the subscription is terminated and the subject is completed
					fetchDataSubject.next();
					fetchDataSubject.complete();
				})
			).subscribe({
				next: (result) => {
					this.globalFunctions.DisplaySnackBar({
						message: "Asset Distribution Updated",
						type: SnackType.Info,
						duration: 1000
					});
					this.siteSubscriptions.UpdateModelList(true);
				},
				error: (error) => { this.globalFunctions.HandleError(error, "Error: this.assetsDataService.postAssetDistribution$", false); },
			});
	}

	/**
	 * patchOldDistribution 
	 * @param distribution distribution 
	 * @param active active 
	 * @param disabled disabled 
	 */
	async patchOldDistribution(distribution: AssetDistribution, active: boolean, disabled: boolean) {

		if (disabled) {
			const OKResult = await firstValueFrom(
				this.globalFunctions.UserConfirm$('Are you sure you want to delete this model?')
			);

			if (!OKResult) { return; }
		}

		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		this.modelsService.patchOldDistribution$(this.selectedAccountUID, this.selectedOrganisationUID, distribution.AssetDistributionID,
			this.selectedAsset.AssetID, active, disabled).pipe(
				takeUntil(fetchDataSubject), distinctUntilChanged(),
				filter((response) => !!response),
				finalize(() => {
					// Ensure the subscription is terminated and the subject is completed
					fetchDataSubject.next();
					fetchDataSubject.complete();
					this.getAssetDistributions();
				})
			).subscribe({
				next: (result) => {
					if (result[0].Result.includes("OK")) {
						this.globalFunctions.DisplaySnackBar({
							message: "Asset Model Updated",
							type: SnackType.Info,
							duration: 1000
						});
						this.refreshModels();
					}
				},
				error: (error) => { this.globalFunctions.HandleError(error, "Error: this.assetsDataService.postAssetDistribution$", false); },
			});
	}

	/**
	 * getActiveModel
	 * @returns 
	 */
	getActiveModel(): any {
		let model = this.assetDistributions.find(x => x.MostRecent === true);
		if (model) {
			this.noActiveModel = false;
			return model;
		} else {
			this.noActiveModel = true;
			return this.assetDistributions[0];
		}
	}

	/**
	 * getInitialModel
	 */
	getInitialModel() {
		if (this.selectedModelType?.ModelTypeID == 1) {
			let findActive = this.assetDistributions.find((x: AssetDistribution) => x.MostRecent == true);
			if (findActive) {
				this.selectedModel = findActive;
			} else {
				this.selectedModel = this.assetDistributions[0];
			}
		}
		this.setOtherModels();
	}

	/**
	 * selectModel
	 * @param model model  
	 */
	selectModel(model: any) {
		this.selectedModel = model;
		this.setOtherModels();
	}

	/**
	 * setOtherModels
	 */
	setOtherModels() {
		if (this.selectedModelType?.ModelTypeID == 1) {
			this.otherModels = this.assetDistributions;
			// this.otherModels = this.assetDistributions.filter((x: AssetDistribution) => x.AssetDistributionID != this.selectedModel.AssetDistributionID);
			this.activeModels = this.assetDistributions.filter((x: AssetDistribution) => x.MostRecent);
		}
		if (this.listbox) {
			this.listbox.refresh();
		}
	}

	isSelectedModel(model: any): boolean {
		let result: boolean = false;
		if (this.selectedModelType?.ModelTypeID == 1) {
			result = model.AssetDistributionID == this.selectedModel.AssetDistributionID;
		}
		return result;
	}
}
